//https://mui.com/material-ui/react-avatar/
import React, { useEffect, useState } from "react";
import ProfileIcon from "../../assets/images/onboarding/profile/profile-pic.svg";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

import { ReactComponent as TickIcon } from "../../assets/icons/components/avtar-badge/Status.svg";
import { ReactComponent as CrossIcon } from "../../assets/icons/components/avtar-badge/Status(1).svg";
import { ReactComponent as LockIcon } from "../../assets/icons/components/avtar-badge/Status(2).svg";
import { ReactComponent as OnlineIcon } from "../../assets/icons/components/avtar-badge/Presence.svg";
import { ReactComponent as ExternalIcon } from "../../assets/icons/Views/dashboard/assessments/external-link.svg";
import { useDispatch } from "react-redux";
import { getResource, getPublicResource } from "../../redux/thunks/Resource";
import { stringToColour } from "../../utils/utilities";
import CustomTooltip from "../common/CustomTooltip";

const resourceUrlCache = new Map();
const resourceRequestCache = new Map();
const failedResourceIds = new Set();

export function GetAvtarImage(props) {
  const {
    size,
    height,
    width,
    src,
    className,
    truncateIcon,
    image,
    turnOnBGColors,
    nameInitals,
    fontSize,
    onClick,
    isForPublic,
    assessID,
  } = props;

  const [userImage, setUserImage] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;
    const resourceId = src?.image_id;

    const getImage = async () => {
      setImageLoadFailed(false);

      if (image) {
        if (isMounted) {
          setUserImage(image);
        }
        return;
      }

      if (!resourceId || failedResourceIds.has(resourceId)) {
        if (isMounted) {
          setUserImage(null);
        }
        return;
      }

      if (resourceUrlCache.has(resourceId)) {
        if (isMounted) {
          setUserImage(resourceUrlCache.get(resourceId));
        }
        return;
      }

      const requestPromise =
        resourceRequestCache.get(resourceId) ||
        dispatch(
          isForPublic
            ? getPublicResource({
                data: {
                  resource_id: resourceId,
                  assess_id: assessID,
                },
              })
            : getResource({ id: resourceId })
        );

      resourceRequestCache.set(resourceId, requestPromise);

      const imageResult = await requestPromise;
      resourceRequestCache.delete(resourceId);

      if (
        !imageResult.error &&
        imageResult?.meta?.requestStatus === "fulfilled" &&
        imageResult?.payload?.url
      ) {
        resourceUrlCache.set(resourceId, imageResult.payload.url);
        if (isMounted) {
          setUserImage(imageResult.payload.url);
        }
        return;
      }

      if (isMounted) {
        setUserImage(null);
      }
    };

    getImage();

    return () => {
      isMounted = false;
    };
  }, [assessID, dispatch, image, isForPublic, src?.image_id]);

  const handleImageError = () => {
    if (src?.image_id) {
      resourceUrlCache.delete(src.image_id);
      resourceRequestCache.delete(src.image_id);
      failedResourceIds.add(src.image_id);
    }
    setImageLoadFailed(true);
    setUserImage(null);
  };

  // If the image is not available, fallback to initials or name
  const renderAvatarContent = () => {
    if (src?.image_id && userImage && !imageLoadFailed) {
      return (
        <img
          className="custom-avatar-img"
          src={userImage}
          alt="user-avatar"
          onError={handleImageError}
        />
      );
    } else if (nameInitals) {
      return `${nameInitals}`.toUpperCase();
    } else if (truncateIcon) {
      return src?.name;
    } else if (src?.name) {
      return src?.name[0];
    } else if (src?.email) {
      return src.email.slice(0, 2).toUpperCase();
    } else {
      return "";
    }
  };

  return (
    <div
      className={`custom-avatar-badge ${className}`}
      onClick={onClick}
      style={{
        ...(turnOnBGColors
          ? { backgroundColor: stringToColour(src?.name) }
          : {}),
        ...(fontSize ? { fontSize: fontSize } : {}),
        ...(size === "md"
          ? { width: 72, height: 72 }
          : size === "custom"
          ? { width: width, height: height }
          : { width: 72, height: 72 }),
        position: "relative",
      }}
    >
      {renderAvatarContent()}
    </div>
  );
}

export default function AvtarBadge(props) {
  const {
    size,
    status,
    varrient,
    users,
    height,
    width,
    src,
    className,
    name,
    turnOnBGColors,
    showTooltip,
    userCount,
  } = props;

  const handleImageError = (event) => {
    event.target.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAEklEQVR42mJ8/w8AAwAB/EP8mH8JAAAAAElFTkSuQmCC";
  };
  const history = useHistory();

  const MAX_BADGES = 5; // 4 avatars + 1 count badge

  const getTruncatedIcons = (users, userCount) => {
    // Always show up to (MAX_BADGES - 1) users
    return users.slice(0, MAX_BADGES - 1);
  };

  return (
    <>
      {varrient === "multiple-badges" ? (
        <div className="avatar-group">
          {getTruncatedIcons(users, userCount).map((user, i) => {
            if (!user.isUserCountBadge) {
              return showTooltip && !user.truncateIcon ? (
                <CustomTooltip
                  key={i}
                  position="top"
                  text={
                    <div
                      style={{ gap: "6px" }}
                      className="d-flex align-items-center pointer"
                      onClick={(e) => {
                        history.push(`/dashboard/candidates/${user.id}`);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.email}
                      <ExternalIcon />
                    </div>
                  }
                >
                  <GetAvtarImage
                    alt={user?.name}
                    turnOnBGColors={turnOnBGColors}
                    size="custom"
                    width={36}
                    height={36}
                    src={user}
                    truncateIcon={user.truncateIcon}
                    nameInitals={user?.nameInitals || ""}
                    onClick={(e) => {
                      history.push(`/dashboard/candidates/${user.id}`);
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  />
                </CustomTooltip>
              ) : (
                <GetAvtarImage
                  key={i}
                  alt={user?.name}
                  turnOnBGColors={!user.truncateIcon && turnOnBGColors}
                  size="custom"
                  width={36}
                  height={36}
                  src={user}
                  truncateIcon={user.truncateIcon}
                  nameInitals={user?.nameInitals || ""}
                />
              );
            }
            return null; // Don't render the count badge here
          })}
          {userCount >= MAX_BADGES && (
            <div
              className="custom-avatar-badge body-3"
              style={{
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#222",
                color: "#fff",
                fontSize: 11,
              }}
            >
              +{userCount - (MAX_BADGES - 1)}
            </div>
          )}
        </div>
      ) : (
        <div className="avtar-badge-container">
          {status === "tick" ? (
            <TickIcon
              className="avtar-badge-status"
              style={
                size === "md"
                  ? { height: "27px", width: "27px" }
                  : { height: "17.5px", width: "17.5px" }
              }
            />
          ) : status === "cross" ? (
            <CrossIcon
              className="avtar-badge-status"
              style={
                size === "md"
                  ? { height: "27px", width: "27px" }
                  : { height: "17.5px", width: "17.5px" }
              }
            />
          ) : status === "lock" ? (
            <LockIcon
              className="avtar-badge-status"
              style={
                size === "md"
                  ? { height: "27px", width: "27px" }
                  : { height: "17.5px", width: "17.5px" }
              }
            />
          ) : status === "online" ? (
            <OnlineIcon
              className="avtar-badge-status"
              style={
                size === "md"
                  ? { height: "14px", width: "14px" }
                  : { height: "8px", width: "8px" }
              }
            />
          ) : null}
          {/* <Avatar
							className={className}
							alt={src}
							src={src ? src : '/static/images/avatar/1.jpg'}
							sx={size === 'md' ? { width: 72, height: 72 } : size === 'custom' ? { width: width, height: height } : { width: 40, height: 40 }} /> */}
          <div
            className="custom-avatar-badge"
            style={{
              ...(size === "md"
                ? { width: 72, height: 72 }
                : size === "custom"
                ? { width: width, height: height }
                : { width: 40, height: 40 }),
              background: "transparent",
            }}
          >
            <img
              className="custom-avatar-img"
              alt={name?.charAt(0)}
              src={src ? src : ProfileIcon}
              onError={handleImageError}
            />
          </div>
        </div>
      )}
    </>
  );
}
